/**
 * The screen a student sees before a lesson starts.
 *
 * One component for what used to be three near-identical ones —
 * InitializationModal, LearnAgainModal and ResumeRestartModal — which differed
 * only in the buttons at the bottom. Everything else (lesson title, language
 * and voice selectors, retake links, the two responsive layouts) was copied
 * between them, so every fix had to be applied three times.
 *
 * The buttons are now data: the caller passes `actions`, and this file has no
 * opinion about which route it is serving.
 */

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import Modal, { ModalSize } from "./Modal";
import ConfirmationModal from "./ConfirmationModal";
import { useModalStore, ModalType } from "@/store/useModalStore";
import { useForm } from "@/features/tutor/context/FormContext";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useVoicePlayer } from "@/hooks/useVoicePlayer";
import {
  AUDIO_VOICE_MAP,
  toVoicePreviewOption,
  getNamePronunciationKey,
  resolveVoiceForLanguage,
} from "@/constants/audioVoice";
import {
  isResumeLockedByLanguage,
  resolveSessionLanguageSeed,
} from "@/features/tutor/vo-demo/contracts/resumeContracts";
import {
  useNamePronunciationAudio,
  type NamePronunciationEntryResponse,
} from "@/hooks/useNamePronunciationAudio";
import api from "@/utils/axios-utils";
import { useViewport } from "@/hooks/useViewport";
import ALL_LANGUAGE_OPTIONS from "@/constants/multilingual";
import { DefaultLayout } from "./SessionStartModalLayout";
import { applyResumeLanguageLock } from "./sessionStartModalContract";
import {
  requestMicPermissionFromGesture,
  waitForPendingMicPermission,
} from "@/features/tutor/vo-demo/audio/micPermissionGestureRequest";
import type {
  SessionStartAction,
  SessionStartActionId,
  SessionStartModalProps,
  SharedProps,
} from "./sessionStartModalContract";

export type {
  SessionStartAction,
  SessionStartActionId,
  SessionStartModalProps,
} from "./sessionStartModalContract";

// Module scope: it has to survive this modal unmounting and remounting around
// a Change* detour, or the seed below would fire again and undo whatever the
// student just chose.
let seededForLessonId: number | string | null = null;

// ─── Main component ──────────────────────────────────────────────────────────

const SessionStartModal = ({
  isOpen,
  onClose,
  // Defaulted rather than required: modalProps in the store is untyped, so a
  // caller that forgets this would crash the render instead of showing a
  // screen the student can at least close.
  actions = [],
  retakeItems = [],
  onRetakeAssessment,
  sessionLanguage,
  lessonId,
}: SessionStartModalProps) => {
  const { openModal } = useModalStore();
  const { isMobileLandscape } = useViewport();
  const { formData, updateField } = useForm();
  const currentLessonName = useSlideStore((state) => state.currentLessonName);
  const [submitting, setSubmitting] = useState(false);
  const [pendingActionId, setPendingActionId] =
    useState<SessionStartActionId | null>(null);
  const [retakeDropdownOpen, setRetakeDropdownOpen] = useState(false);
  const [confirmingAction, setConfirmingAction] =
    useState<SessionStartAction | null>(null);
  const [namePronunciationEntries, setNamePronunciationEntries] = useState<
    Record<string, NamePronunciationEntryResponse>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    void api
      .get<Record<string, NamePronunciationEntryResponse>>(
        "/name-pronunciation/entries",
      )
      .then((res) => {
        if (cancelled) return;
        setNamePronunciationEntries(res.data);
      })
      .catch(() => {
        // Non-critical — icon simply won't show if fetch fails
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const pronVoice = formData.voice?.voiceId
    ? AUDIO_VOICE_MAP[formData.voice.voiceId]
    : null;
  const pronKey =
    pronVoice && formData.language?.value
      ? getNamePronunciationKey(pronVoice, formData.language.value)
      : null;
  const savedEntry = pronKey ? namePronunciationEntries[pronKey] : null;
  const isVoiceTrained = Boolean(savedEntry);

  const { playCurrent, stop: stopPronunciation } = useNamePronunciationAudio();

  const handleSpeakName = () => {
    if (!savedEntry || !formData.voice?.voiceId || !formData.language?.value)
      return;
    const voice = AUDIO_VOICE_MAP[formData.voice.voiceId];
    if (!voice) return;
    // savedEntry carries the phonemes playCurrent needs to hit the S3 cache.
    void playCurrent(
      savedEntry.surfaceText,
      voice,
      formData.language.value,
      savedEntry,
    );
  };

  // Stop pronunciation audio when the modal unmounts (ModalRoot replaces modals
  // by unmounting rather than setting isOpen=false).
  useEffect(() => () => stopPronunciation(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentAudioVoice = formData.voice
    ? AUDIO_VOICE_MAP[formData.voice.voiceId]
    : null;
  const currentVoiceOption = currentAudioVoice
    ? toVoicePreviewOption(currentAudioVoice, formData.language?.value)
    : null;

  const { voicePreviewRef, handleProgress, handlePlayStateChange } =
    useVoicePlayer(currentVoiceOption);

  const sessionLanguageOption = useMemo(
    () =>
      sessionLanguage
        ? (ALL_LANGUAGE_OPTIONS.find((l) => l.value === sessionLanguage) ??
          null)
        : null,
    [sessionLanguage],
  );

  // Before paint, not after: a student whose stored preference differs from
  // the session's language would otherwise see Resume locked for one frame
  // without having touched anything, note and all.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const { shouldMarkSeeded, language } = resolveSessionLanguageSeed({
      sessionLanguage: sessionLanguageOption?.value ?? null,
      selectedLanguage: formData.language?.value,
      lessonId,
      seededForLessonId,
    });
    if (shouldMarkSeeded) seededForLessonId = lessonId ?? null;
    if (!language || !sessionLanguageOption) return;
    updateField("language", sessionLanguageOption);
    const voiceId = resolveVoiceForLanguage(language, formData.voice?.voiceId);
    if (voiceId !== formData.voice?.voiceId) {
      updateField("voice", {
        voiceId,
        speed: formData.voice?.speed ?? "medium",
      });
    }
  }, [isOpen, sessionLanguageOption, lessonId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resumeLockedByLanguage = isResumeLockedByLanguage(
    sessionLanguage,
    formData.language?.value,
  );
  const sessionLanguageLabel = sessionLanguageOption?.label ?? null;
  // Applied to the action rather than to a button, because which button
  // resumes is the caller's business and this modal no longer knows.
  const effectiveActions = useMemo(
    () =>
      applyResumeLanguageLock(actions, {
        locked: resumeLockedByLanguage,
        sessionLanguageLabel,
        selectedLanguageLabel: formData.language?.label ?? null,
      }),
    [actions, resumeLockedByLanguage, sessionLanguageLabel, formData.language],
  );

  // Everything the Change* modals need to hand back so this screen re-opens as
  // it was. Passed wholesale rather than field by field: the older per-field
  // form silently dropped the assessment props, and the retake link with them.
  const returnModalProps = {
    actions,
    retakeItems,
    onRetakeAssessment,
    sessionLanguage,
    lessonId,
  };

  const openChangeModal = (type: ModalType) => {
    if (submitting) return;
    stopPronunciation();
    openModal(type, {
      returnModalType: ModalType.SESSION_START,
      returnModalProps,
    });
  };

  const openChangeLanguage = () => openChangeModal(ModalType.CHANGE_LANGUAGE);
  const openChangeCharacter = () => openChangeModal(ModalType.CHANGE_CHARACTER);
  const openChangeVoice = () => openChangeModal(ModalType.CHANGE_VOICE);

  const startAction = (action: SessionStartAction) => {
    if (submitting) return;
    // Must stay synchronous in the tap (see micPermissionGestureRequest); the
    // boot starts only after the student answers the prompt.
    requestMicPermissionFromGesture();
    stopPronunciation();
    setPendingActionId(action.id);
    setSubmitting(true);
    void waitForPendingMicPermission()
      .then(action.onSelect)
      // Swallowed on purpose: the caller reports its own failures, and the UI
      // has to unlock either way or the student is stranded on a dead button.
      .catch(() => undefined)
      .finally(() => {
        setSubmitting(false);
        setPendingActionId(null);
      });
  };

  const runAction = (action: SessionStartAction) => {
    if (submitting || action.disabledReason) return;
    if (action.confirm) {
      setConfirmingAction(action);
      return;
    }
    startAction(action);
  };

  // Locks the screen for the same reason an action does. The handler closes
  // this modal on its own, but two awaits later; until then every button here
  // is still live.
  const runRetake = onRetakeAssessment
    ? (type: string) => {
        if (submitting) return;
        requestMicPermissionFromGesture();
        stopPronunciation();
        setSubmitting(true);
        void waitForPendingMicPermission()
          .then(() => onRetakeAssessment(type))
          .catch(() => undefined)
          .finally(() => setSubmitting(false));
      }
    : undefined;

  const handleClose = () => {
    if (submitting) return;
    stopPronunciation();
    onClose();
  };

  const sharedProps: SharedProps = {
    currentLessonName,
    formData,
    actions: effectiveActions,
    submitting,
    pendingActionId,
    runAction,
    retakeItems,
    retakeDropdownOpen,
    setRetakeDropdownOpen,
    voicePreviewRef,
    handleProgress,
    handlePlayStateChange,
    openChangeLanguage,
    openChangeCharacter,
    openChangeVoice,
    handleClose,
    runRetake,
    isVoiceTrained,
    handleSpeakName,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      showCloseButton={false}
      size={ModalSize.sm}
      className={`et-lesson-entry-modal p-0${isMobileLandscape ? " et-entry-landscape" : ""}`}
    >
      <div className="relative flex flex-1 min-h-0 w-full">
        <DefaultLayout {...sharedProps} />
      </div>
      {confirmingAction?.confirm && (
        <ConfirmationModal
          title={confirmingAction.confirm.title}
          message={confirmingAction.confirm.message}
          confirmLabel={confirmingAction.confirm.confirmLabel}
          cancelLabel="Cancel"
          onConfirm={() => {
            // ConfirmationModal focuses its confirm button and binds Enter, so
            // a held key can fire this twice before the dialog unmounts.
            if (submitting) return;
            const action = confirmingAction;
            setConfirmingAction(null);
            startAction(action);
          }}
          onCancel={() => {
            if (submitting) return;
            setConfirmingAction(null);
          }}
          showDoNotShowAgain={false}
        />
      )}
    </Modal>
  );
};

export default SessionStartModal;
